import { ApiError, type ApiOpts } from '@/lib/api';
import type { StudyDocumentDetail, StudySession } from '@/pages/learn/study/studyApi';
import { samplePdfUrl, samplePageImage, type SampleSourcePage } from './sampleSourceAssets';

/** Authored fictional Year 8 history, never a live AI answer or curriculum-alignment claim. */
export const SCIENCE_EXPLANATIONS = [
  {
    subject: 'physics',
    name: 'Physics',
    key: 'steady-walk',
    title: 'A steady walk: reading motion graphs',
    question:
      'Why does a straight rising position graph mean steady speed, rather than speeding up?',
    paragraphs: [
      'Class investigation: Alex walks along a straight path at a steady 1.5 metres per second, starting at position 0 metres. Record position after 0, 2, 4 and 6 seconds.',
      'Measurements: time (s) 0, 2, 4, 6; position (m) 0, 3, 6, 9. Each two-second interval adds the same three metres.',
      'Discuss: How do the position-time and velocity-time graphs differ? Predict the position at 10 seconds. Explain why a rising position graph does not, by itself, mean speeding up.',
      'Tool investigation: In Motion Graph Lab choose Steady walk. Pause and move Time to 10 s. Compare the position and velocity readouts with your prediction.',
    ],
    explanation:
      'A position graph tells you where Alex is, not how fast the line moves on the screen. In this example, every two seconds adds three metres. Equal distances in equal times mean constant velocity in this straight-line journey.\n\nThe position-time graph is a straight rising line: its slope stays the same. The velocity-time graph is horizontal at 1.5 m/s. A higher position is not the same as a higher velocity.\n\nUse position = starting position + velocity × time: at 6 seconds, 0 + 1.5 × 6 = 9 metres. Predict the position at 10 seconds before revealing it.\n\nOpen Motion Graph Lab below, choose Steady walk and move Time to 10 s. Check for position 15 m and velocity 1.5 m/s. Then return here: which graph shows that the speed stayed steady? The tool values are yours to change; this source is not imported automatically.',
    tool: 'motion-graphs',
  },
  {
    subject: 'chemistry',
    name: 'Chemistry',
    key: 'element-types',
    title: 'Elements: type is different from state',
    question: 'Magnesium and sulfur are both solids. Does that mean they are both metals?',
    paragraphs: [
      'Class reference: Magnesium has the symbol Mg. Sulfur has the symbol S. An element symbol identifies a particular element; it is not an abbreviation for its state.',
      'At room conditions (about 25 degrees Celsius and normal air pressure), both magnesium and sulfur are solids. Magnesium is a metal; sulfur is a non-metal.',
      'Discuss: Can state alone tell us whether an element is a metal? Keep two headings in your comparison: type of element, and state at room conditions.',
      'Tool investigation: In Periodic Table Explorer, use the Year 8 view. Search Magnesium and open Mg; then clear the search, find Sulfur and open S. Compare the two labelled summaries.',
    ],
    explanation:
      'No: solid describes a state, while metal describes a type of element. They answer different questions. Two substances can share a state without belonging to the same type.\n\nFor this comparison, keep the room conditions fixed. Magnesium (Mg) is a metal and a solid. Sulfur (S) is a non-metal and a solid. So “both solid” does not imply “both metal”.\n\nThe periodic table helps you locate and compare elements. In the Year 8 view, concentrate on Type of element and State at room conditions; you do not need a later-year atomic model for this task.\n\nOpen Periodic Table Explorer below. Search Magnesium, open Mg, and read the two headings. Close its panel, clear the search, then find Sulfur and open S. Predict which heading changes before looking. Return here and explain why sorting all solids into the metal group would be a mistake.',
    tool: 'periodic-table',
  },
  {
    subject: 'biology',
    name: 'Biology',
    key: 'cell-structures',
    title: 'Cells: shared parts and plant structures',
    question: 'Does an animal cell have a nucleus? Which parts help distinguish a plant cell?',
    paragraphs: [
      'Class reference: This activity compares typical animal and plant cells. Both have a cell membrane, cytoplasm and a nucleus. The nucleus contains genetic instructions and helps control cell activities.',
      'A typical plant cell also has a cell wall outside its membrane and a large central vacuole. Green leaf cells contain chloroplasts. Not every plant cell has chloroplasts; root cells are a useful counterexample.',
      'Discuss: A nucleus is not a feature that separates plant cells from animal cells. Compare shared structures first, then name a structure shown only in the plant model.',
      'Tool investigation: Open Cell Explorer. Examine the animal cell and select its nucleus. Switch to the plant cell and compare the cell wall and membrane. Use the labels rather than relying only on colour.',
    ],
    explanation:
      'Yes: the typical animal cell shown here has a nucleus, and so does the typical plant cell. A nucleus is therefore a shared part, not a test for “plant or animal”. It contains genetic instructions and helps control cell activities.\n\nCompare the outside of each model. Both have a cell membrane. The plant cell has an additional cell wall outside that membrane. These are separate structures, even when a flat drawing makes their edges look close together.\n\nThe plant model also shows a large central vacuole. Green leaf cells have chloroplasts, but do not conclude that every plant cell must have them: root cells are a useful exception. These models represent typical cells rather than every cell in an organism.\n\nOpen Cell Explorer below. Select the nucleus in the animal cell, then change to the plant cell and inspect the cell wall. Read each structure label. Return here: name one shared part and one plant-model part, and explain why colour alone is not enough to identify them.',
    tool: 'cell-explorer',
  },
] as const;

export const scienceExplanationPath = (key: string) =>
  `/learn/study/sessions/example-session-${key}?message=example-reply-${key}`;

export function createSampleScienceExplanations(
  documents: StudyDocumentDetail[],
  termId: string,
  date: string,
) {
  const stamp = `${date}T01:00:00.000Z`;
  const records = SCIENCE_EXPLANATIONS.map((item) => {
    const source: SampleSourcePage[] = [{ title: item.title, paragraphs: [...item.paragraphs] }];
    const pdf = samplePdfUrl(source);
    const document: StudyDocumentDetail = {
      id: `example-material-${item.key}`,
      file_name: `Alex-${item.key}-sample.pdf`,
      display_name: item.title,
      mime_type: 'application/pdf',
      size_bytes: pdf.bytes,
      status: 'ready',
      classification_state: 'learner_confirmed',
      term_id: termId,
      subject: { subject_key: item.subject, display_name: item.name },
      created_at: stamp,
      read_url: pdf.url,
      read_url_expires_in: 3600,
      source_revision: 'fictional-prepared-source-v1',
      thumbnail: { kind: 'pdf', url: pdf.url, expires_in: 3600 },
    };
    documents.push(document);
    let session: Promise<StudySession> | undefined;
    return {
      item,
      document,
      read: () =>
        (session ??= samplePageImage(0, source).then(
          (page): StudySession => ({
            id: `example-session-${item.key}`,
            goal: 'understand',
            status: 'completed',
            highest_hint_level: 1,
            help_policy: { h4_allowed: false },
            summary: null,
            review_due_at: null,
            completed_at: stamp,
            created_at: stamp,
            term: { id: termId, term_number: 4, label: 'Term 4' },
            subject: document.subject!,
            document: {
              ...document,
              tutor_source_ready: true,
              selected_page: {
                ...page,
                page_number: 1,
                provenance: 'client_pdfjs',
                read_url_expires_in: 3600,
              },
              focused_region: null,
            },
            learning: {
              id: `example-learning-${item.key}`,
              started_at: stamp,
              ended_at: stamp,
              stars_used: 0,
              llm_calls: 0,
            },
            attempt: { content: item.question, submitted_at: stamp, updated_at: stamp },
            source_confirmation: null,
            generated_practice: null,
            analysis_question: null,
            messages: [
              {
                id: `example-question-${item.key}`,
                role: 'user',
                content: item.question,
                stars_charged: 0,
                created_at: stamp,
              },
              {
                id: `example-reply-${item.key}`,
                role: 'assistant',
                content: `Pre-generated example explanation — not a live AI reply.\n\n${item.explanation}`,
                stars_charged: 0,
                created_at: stamp,
              },
            ],
          }),
        )),
    };
  });
  return {
    history: records.map(({ item }) => ({
      id: `example-history-${item.key}`,
      title: item.title,
      at: stamp,
      href: scienceExplanationPath(item.key),
      evidence: 'Prepared fictional explanation · no real AI receipt',
      subject_key: item.subject,
    })),
    async handle(path: string, opts: ApiOpts) {
      const p = new URL(path, 'https://experience.invalid').pathname;
      const record = records.find(({ item }) =>
        p.startsWith(`/study/sessions/example-session-${item.key}`),
      );
      if (!record) return null;
      if (
        p !== `/study/sessions/example-session-${record.item.key}` ||
        (opts.method ?? 'GET') !== 'GET'
      )
        throw new ApiError(
          403,
          'STUDY_EXPERIENCE_ONLY',
          'This is a saved fictional explanation. No live AI request was sent.',
        );
      return { value: await record.read() };
    },
  };
}
